import type { Client } from "#/types/client";
import { UnitSystem } from "#/types/client";

/**
 * Centralized unit conversion utilities, mirroring dateTimeUtils.ts: values
 * are always stored canonically (kg, cm, m3) and only converted for
 * display/input according to the client's `unitSystem` preference.
 */

const KG_PER_LB = 0.45359237;
const CM_PER_IN = 2.54;
const M3_PER_FT3 = 0.0283168466;

export const getUnitSystem = (client: Client | null | undefined): UnitSystem =>
  client?.unitSystem || UnitSystem.METRIC;

const toDisplay = (canonical: number | undefined, unitSystem: UnitSystem, factor: number) =>
  canonical == null ? canonical : unitSystem === UnitSystem.IMPERIAL ? canonical / factor : canonical;

const toCanonical = (display: number | undefined, unitSystem: UnitSystem, factor: number) =>
  display == null ? display : unitSystem === UnitSystem.IMPERIAL ? display * factor : display;

/** Canonical kg <-> displayed kg/lb. */
export const weightToDisplay = (kg: number | undefined, unitSystem: UnitSystem) =>
  toDisplay(kg, unitSystem, KG_PER_LB);
export const weightToCanonical = (value: number | undefined, unitSystem: UnitSystem) =>
  toCanonical(value, unitSystem, KG_PER_LB);

/** Canonical cm <-> displayed cm/inches. */
export const dimensionToDisplay = (cm: number | undefined, unitSystem: UnitSystem) =>
  toDisplay(cm, unitSystem, CM_PER_IN);
export const dimensionToCanonical = (value: number | undefined, unitSystem: UnitSystem) =>
  toCanonical(value, unitSystem, CM_PER_IN);

/** Canonical m3 <-> displayed m3/ft3. */
export const volumeToDisplay = (m3: number | undefined, unitSystem: UnitSystem) =>
  toDisplay(m3, unitSystem, M3_PER_FT3);
export const volumeToCanonical = (value: number | undefined, unitSystem: UnitSystem) =>
  toCanonical(value, unitSystem, M3_PER_FT3);

export const weightUnitLabel = (unitSystem: UnitSystem) =>
  unitSystem === UnitSystem.IMPERIAL ? "lb" : "kg";
export const dimensionUnitLabel = (unitSystem: UnitSystem) =>
  unitSystem === UnitSystem.IMPERIAL ? "in" : "cm";
export const volumeUnitLabel = (unitSystem: UnitSystem) =>
  unitSystem === UnitSystem.IMPERIAL ? "ft³" : "m³";
